import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@roger/ui-components';
import {
  getOrderCms485Form,
  getOrderCms485Prefill,
  listCms485OasisVisits,
  updateOrder,
} from '../../services/api';
import type {
  Cms485OasisVisitOption,
  OrderResponse,
  TaskStatus,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { COMPLETED_STATUSES } from '../../constants/statuses';
import { formatTaskStatusLabel } from './orderUtils';
import type { Cms485FormValues } from '../../types/cms485.types';
import {
  formResponseToFormValues,
  formValuesToOrderData,
  visitToFormValues,
} from '../../utils/cms485Form';
import {
  applyCms485Prefill,
  clearPrefillFields,
  getPrefillConfirmActionLabel,
  getPrefillConfirmDescription,
  getPrefillFieldsForMode,
  getSectionPrefillButtonLabel,
  hasPrefillContentForMode,
  isOasisPrefillMode,
  prefillRequiresOasisVisit,
  type PrefillMode,
} from '../../utils/cms485Prefill';

const DEFAULT_HOMEBOUND_STATEMENT =
  'The patient is homebound because they are unable to leave home without considerable and taxing effort. The patient requires the assistance of another person or the use of supportive devices to leave the home.';

type ConfirmOverwriteAction =
  | { type: 'prefill'; mode: PrefillMode }
  | { type: 'homebound-template' };

function getConfirmOverwriteTitle(action: ConfirmOverwriteAction): string {
  return action.type === 'homebound-template'
    ? 'Replace homebound statement?'
    : 'Replace prefilled fields?';
}

function getConfirmOverwriteDescription(
  action: ConfirmOverwriteAction
): string {
  if (action.type === 'homebound-template') {
    return 'Inserting the standard homebound certification template will replace the current text in this field.';
  }
  return getPrefillConfirmDescription(action.mode);
}

function getConfirmOverwriteActionLabel(
  action: ConfirmOverwriteAction
): string {
  if (action.type === 'homebound-template') {
    return 'Insert standard template';
  }
  return getPrefillConfirmActionLabel(action.mode);
}

interface CMS485OrderFormProps {
  visit: OrderResponse;
  patientId: string;
}

const diagnosisRowSchema = z.object({
  code: z.string(),
  description: z.string(),
  date: z.string(),
});

const allergyRowSchema = z.object({
  substance: z.string(),
  reaction: z.string(),
});

const schema = z.object({
  linked_oasis_visit_id: z.string().min(1, 'OASIS visit selection is required'),
  soc_date: z.string(),
  primary_language: z.string(),
  risk_profile: z.string(),
  primary_diagnosis_code: z.string(),
  primary_diagnosis_description: z.string(),
  primary_diagnosis_date: z.string(),
  secondary_diagnoses: z.array(diagnosisRowSchema),
  mental_status: z.string(),
  dme_supplies: z.string(),
  prognosis: z.string(),
  safety_measures: z.string(),
  nutritional_requirements: z.string(),
  functional_limitations: z.string(),
  activities_permitted: z.string(),
  medications: z.string(),
  allergies: z.array(allergyRowSchema),
  orders_and_treatments: z.string(),
  homebound_certification_statement: z.string(),
  goals: z.string(),
});

type FormValues = Cms485FormValues;

const isOasisVisitCompleted = (status: string | null | undefined): boolean =>
  status != null && COMPLETED_STATUSES.has(status as TaskStatus);

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className='text-lg font-semibold text-foreground border-b pb-2'>
      {children}
    </h3>
  );
}

export const CMS485OrderForm: React.FC<CMS485OrderFormProps> = ({
  visit,
  patientId,
}) => {
  const showError = useStore(state => state.showError);
  const [oasisVisits, setOasisVisits] = useState<Cms485OasisVisitOption[]>([]);
  const [loadingOasisVisits, setLoadingOasisVisits] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const formSyncedOrderIdRef = useRef<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [prefillSourceVisitId, setPrefillSourceVisitId] = useState<
    string | null
  >(visit.linked_oasis_visit_id ?? null);
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);
  const [pendingConfirmAction, setPendingConfirmAction] =
    useState<ConfirmOverwriteAction | null>(null);
  const orderDataLastUpdatedRef = useRef<string | null>(
    visit.order_data_last_updated ?? null
  );

  useEffect(() => {
    orderDataLastUpdatedRef.current = visit.order_data_last_updated ?? null;
  }, [visit.id, visit.order_data_last_updated]);

  useEffect(() => {
    formSyncedOrderIdRef.current = null;
  }, [visit.id]);

  const {
    control,
    register,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: visitToFormValues(visit),
  });

  const {
    fields: secondaryDiagnosisFields,
    append: appendSecondaryDiagnosis,
    remove: removeSecondaryDiagnosis,
  } = useFieldArray({ control, name: 'secondary_diagnoses' });

  const {
    fields: allergyFields,
    append: appendAllergy,
    remove: removeAllergy,
  } = useFieldArray({ control, name: 'allergies' });

  const loadForm = useCallback(async () => {
    setLoadingForm(true);
    try {
      const res = await getOrderCms485Form({
        path: { order_id: visit.id },
      });
      if (res.error || !res.data) {
        console.error('Failed to load CMS-485 form:', res.error);
        showError(
          'Load Failed',
          'Unable to load CMS-485 form data. Please try again.'
        );
        return;
      }
      if (formSyncedOrderIdRef.current !== visit.id) {
        reset(formResponseToFormValues(res.data, visit));
        formSyncedOrderIdRef.current = visit.id;
      }
    } catch (err) {
      console.error('Failed to load CMS-485 form:', err);
      showError(
        'Load Failed',
        'Unable to load CMS-485 form data. Please try again.'
      );
    } finally {
      setLoadingForm(false);
    }
  }, [visit, reset, showError]);

  useEffect(() => {
    void loadForm();
  }, [loadForm, visit.order_data_last_updated]);

  // Fetch CMS-485-eligible OASIS visits (Kinnser +/-10 day window, incl. prior episode tail)
  useEffect(() => {
    const fetchOasisVisits = async () => {
      setLoadingOasisVisits(true);
      try {
        const res = await listCms485OasisVisits({
          path: { patient_id: patientId },
          query: { episode_id: visit.episode_id },
        });
        if (res.error) {
          console.error('Failed to fetch OASIS visits:', res.error);
          showError(
            'Load Failed',
            'Unable to load OASIS visits for this order. Please try again.'
          );
          return;
        }
        if (res.data) {
          setOasisVisits(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch OASIS visits:', err);
        showError(
          'Load Failed',
          'Unable to load OASIS visits for this order. Please try again.'
        );
      } finally {
        setLoadingOasisVisits(false);
      }
    };
    fetchOasisVisits();
  }, [patientId, visit.episode_id, showError]);

  const applyStandardHomeboundTemplate = () => {
    setValue('homebound_certification_statement', DEFAULT_HOMEBOUND_STATEMENT, {
      shouldDirty: true,
    });
    setConfirmOverwriteOpen(false);
    setPendingConfirmAction(null);
  };

  const requestStandardHomeboundTemplate = () => {
    if (getValues().homebound_certification_statement?.trim()) {
      setPendingConfirmAction({ type: 'homebound-template' });
      setConfirmOverwriteOpen(true);
      return;
    }
    applyStandardHomeboundTemplate();
  };

  // Fetch prefill for the selected mode. OASIS-linked modes require a visit id;
  // goals load from episode data; Orders & Treatments from the linked SOC visit.
  // When `clearFirst` is true, existing values for the selected mode are cleared
  // only after a successful response.
  const fetchAndApplyPrefill = async (
    mode: PrefillMode,
    clearFirst: boolean,
    oasisVisitId?: string
  ): Promise<boolean> => {
    setPrefilling(true);
    try {
      const res = await getOrderCms485Prefill({
        path: { order_id: visit.id },
        query: oasisVisitId ? { visit_id: oasisVisitId } : {},
      });
      if (res.error || !res.data) {
        console.error('Failed to fetch CMS-485 prefill:', res.error);
        showError(
          'Prefill Failed',
          isOasisPrefillMode(mode)
            ? 'Unable to load prefilled data from the OASIS visit. Please try again or fill in fields manually.'
            : 'Unable to load prefilled data. Please try again or fill in fields manually.'
        );
        return false;
      }
      const data = res.data;
      const fieldsToApply = getPrefillFieldsForMode(mode);
      if (clearFirst) {
        clearPrefillFields(setValue, fieldsToApply);
      }
      applyCms485Prefill(setValue, data, mode);
      return true;
    } catch (err) {
      console.error('Failed to fetch CMS-485 prefill:', err);
      showError(
        'Prefill Failed',
        isOasisPrefillMode(mode)
          ? 'Unable to load prefilled data from the OASIS visit. Please try again or fill in fields manually.'
          : 'Unable to load prefilled data. Please try again or fill in fields manually.'
      );
      return false;
    } finally {
      setPrefilling(false);
    }
  };

  const linkedVisitId = watch('linked_oasis_visit_id');

  const selectedVisitDiffersFromPrefillSource =
    Boolean(linkedVisitId) && linkedVisitId !== prefillSourceVisitId;

  const handlePrefill = async (mode: PrefillMode) => {
    const oasisVisitId = prefillRequiresOasisVisit(mode)
      ? linkedVisitId
      : undefined;
    if (prefillRequiresOasisVisit(mode) && !oasisVisitId) return;
    const clearFirst = hasPrefillContentForMode(getValues(), mode);
    const ok = await fetchAndApplyPrefill(mode, clearFirst, oasisVisitId);
    if (!ok) return;
    if (oasisVisitId) setPrefillSourceVisitId(oasisVisitId);
    setConfirmOverwriteOpen(false);
    setPendingConfirmAction(null);
  };

  const requestPrefill = (mode: PrefillMode) => {
    if (prefillRequiresOasisVisit(mode) && !linkedVisitId) return;
    if (hasPrefillContentForMode(getValues(), mode)) {
      setPendingConfirmAction({ type: 'prefill', mode });
      setConfirmOverwriteOpen(true);
      return;
    }
    void handlePrefill(mode);
  };

  const handleConfirmOverwrite = () => {
    if (!pendingConfirmAction) return;
    if (pendingConfirmAction.type === 'homebound-template') {
      applyStandardHomeboundTemplate();
      return;
    }
    void handlePrefill(pendingConfirmAction.mode);
  };

  const renderSectionPrefillButton = (
    mode: 'goals' | 'orders_and_treatments'
  ) => (
    <Button
      type='button'
      variant='outline'
      size='sm'
      disabled={
        prefilling || (mode === 'orders_and_treatments' && !linkedVisitId)
      }
      onClick={() => requestPrefill(mode)}
    >
      {getSectionPrefillButtonLabel(mode)}
    </Button>
  );

  useEffect(() => {
    const subscription = watch(formValues => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        // Re-validate the form before saving. Without this gate the backend
        // would auto-advance NOT_STARTED -> SAVED while the form still shows
        // an unresolved error (e.g. "OASIS visit selection is required"),
        // so Order Manager would say "Saved" for an incomplete CMS-485.
        const ok = await trigger();
        if (!ok) return;
        const response = await updateOrder({
          path: { order_id: visit.id },
          body: {
            order_data: formValuesToOrderData(formValues as FormValues),
            order_data_last_updated: orderDataLastUpdatedRef.current,
          },
        });
        if (response.error) {
          showError('Failed to save order. Please try again.');
          return;
        }
        if (response.data?.order_data_last_updated) {
          orderDataLastUpdatedRef.current =
            response.data.order_data_last_updated;
        }
        void loadForm();
      }, 1500);
    });
    return () => {
      subscription.unsubscribe();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [watch, trigger, visit.id, showError, loadForm]);

  return (
    <Card>
      <CardContent className='space-y-6'>
        <div className='space-y-1.5'>
          <Label>OASIS Visit</Label>
          <Controller
            name='linked_oasis_visit_id'
            control={control}
            render={({ field }) => {
              const selectedVisit = oasisVisits.find(
                v => v.visit_id === field.value
              );
              const linkedVisitMissingFromOptions =
                Boolean(field.value) &&
                !loadingOasisVisits &&
                selectedVisit === undefined;
              const selectedIsIncomplete =
                selectedVisit !== undefined &&
                !isOasisVisitCompleted(selectedVisit.status);
              return (
                <>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger selectSize='sm'>
                      <SelectValue
                        placeholder={
                          loadingOasisVisits
                            ? 'Loading...'
                            : 'Select OASIS visit'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {oasisVisits.map(v => {
                        const isCompleted = isOasisVisitCompleted(v.status);
                        const statusLabel =
                          v.status != null
                            ? formatTaskStatusLabel(v.status as TaskStatus)
                            : 'Unknown';
                        return (
                          <SelectItem key={v.visit_id} value={v.visit_id}>
                            <span
                              className={
                                isCompleted
                                  ? undefined
                                  : 'text-muted-foreground'
                              }
                            >
                              {v.display_name}
                              {' · '}
                              {statusLabel}
                              {!isCompleted && ' (not completed)'}
                              {v.from_previous_episode && ' · prior episode'}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {linkedVisitMissingFromOptions && (
                    <p className='text-xs text-amber-600'>
                      Linked OASIS visit is outside the eligible window.
                    </p>
                  )}
                  {selectedIsIncomplete && (
                    <p className='text-xs text-amber-600'>
                      Source visit is not completed. Prefilled data may be
                      incomplete.
                    </p>
                  )}
                </>
              );
            }}
          />
          {linkedVisitId && !prefilling && (
            <div className='flex flex-col gap-2'>
              <p className='text-xs text-muted-foreground'>
                {selectedVisitDiffersFromPrefillSource
                  ? 'Changing the linked visit does not update fields until you reload from OASIS.'
                  : 'Prefill from the linked OASIS visit overwrites matching fields below.'}
              </p>
              <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='shrink-0'
                  onClick={() => requestPrefill('all')}
                >
                  Prefill all data
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='shrink-0'
                  onClick={() => requestPrefill('non-narrative')}
                >
                  Prefill non-narrative data
                </Button>
              </div>
            </div>
          )}
          {(prefilling || loadingForm) && (
            <p className='text-xs text-muted-foreground'>
              {prefilling
                ? 'Loading prefilled data from OASIS visit…'
                : 'Refreshing form preview…'}
            </p>
          )}
          <Dialog
            open={confirmOverwriteOpen}
            onOpenChange={open => {
              setConfirmOverwriteOpen(open);
              if (!open) setPendingConfirmAction(null);
            }}
          >
            <DialogContent className='max-w-md'>
              <DialogHeader>
                <DialogTitle>
                  {pendingConfirmAction
                    ? getConfirmOverwriteTitle(pendingConfirmAction)
                    : 'Replace existing text?'}
                </DialogTitle>
                <DialogDescription>
                  {pendingConfirmAction
                    ? getConfirmOverwriteDescription(pendingConfirmAction)
                    : ''}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    setConfirmOverwriteOpen(false);
                    setPendingConfirmAction(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type='button'
                  size='sm'
                  onClick={handleConfirmOverwrite}
                  disabled={prefilling}
                >
                  {pendingConfirmAction
                    ? getConfirmOverwriteActionLabel(pendingConfirmAction)
                    : 'Confirm'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {errors.linked_oasis_visit_id && (
            <p className='text-xs text-red-500'>
              {errors.linked_oasis_visit_id.message}
            </p>
          )}
        </div>

        <SectionTitle>Patient Information</SectionTitle>
        <div className='grid gap-3 grid-cols-2'>
          <div className='space-y-1.5'>
            <Label>Start of Care Date</Label>
            <Input {...register('soc_date')} placeholder='MM/DD/YYYY' />
          </div>
          <div className='space-y-1.5'>
            <Label>Primary Language</Label>
            <Input
              {...register('primary_language')}
              placeholder='Primary language'
            />
          </div>
        </div>
        <div className='space-y-1.5'>
          <Label>Patient Risk Profile</Label>
          <Textarea
            {...register('risk_profile')}
            placeholder='Enter risk profile'
            textareaSize='sm'
          />
        </div>

        <SectionTitle>Clinical Data</SectionTitle>
        <div className='space-y-2'>
          <Label>Primary Diagnosis</Label>
          <div className='grid gap-3 sm:grid-cols-3'>
            <div className='space-y-1.5'>
              <Label className='text-xs text-muted-foreground'>Code</Label>
              <Input
                {...register('primary_diagnosis_code')}
                placeholder='Diagnosis code'
              />
            </div>
            <div className='space-y-1.5'>
              <Label className='text-xs text-muted-foreground'>
                Description
              </Label>
              <Input
                {...register('primary_diagnosis_description')}
                placeholder='Diagnosis description'
              />
            </div>
            <div className='space-y-1.5'>
              <Label className='text-xs text-muted-foreground'>Date</Label>
              <Input
                {...register('primary_diagnosis_date')}
                placeholder='MM/DD/YYYY'
              />
            </div>
          </div>
        </div>

        <div className='space-y-1.5'>
          <div className='flex items-center justify-between gap-2'>
            <Label>Secondary/Other Diagnosis</Label>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 px-2 text-xs text-muted-foreground'
              onClick={() =>
                appendSecondaryDiagnosis({
                  code: '',
                  description: '',
                  date: '',
                })
              }
            >
              Add diagnosis
            </Button>
          </div>
          {secondaryDiagnosisFields.length ? (
            <div className='divide-y divide-border/50'>
              {secondaryDiagnosisFields.map((field, index) => (
                <div
                  key={field.id}
                  className='grid gap-2 py-2 sm:grid-cols-[1fr_2fr_1fr_auto] sm:items-end first:pt-0'
                >
                  <div className='space-y-1'>
                    <Label className='text-xs text-muted-foreground'>
                      Code
                    </Label>
                    <Input
                      {...register(`secondary_diagnoses.${index}.code`)}
                      placeholder='Code'
                      inputSize='sm'
                    />
                  </div>
                  <div className='space-y-1'>
                    <Label className='text-xs text-muted-foreground'>
                      Description
                    </Label>
                    <Input
                      {...register(`secondary_diagnoses.${index}.description`)}
                      placeholder='Description'
                      inputSize='sm'
                    />
                  </div>
                  <div className='space-y-1'>
                    <Label className='text-xs text-muted-foreground'>
                      Date
                    </Label>
                    <Input
                      {...register(`secondary_diagnoses.${index}.date`)}
                      placeholder='MM/DD/YYYY'
                      inputSize='sm'
                    />
                  </div>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='h-8 px-2 text-xs text-muted-foreground'
                    onClick={() => removeSecondaryDiagnosis(index)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className='text-xs text-muted-foreground'>
              No secondary diagnoses.
            </p>
          )}
        </div>

        <div className='space-y-1.5'>
          <Label>Mental Status</Label>
          <Textarea
            {...register('mental_status')}
            placeholder='Enter mental status'
            textareaSize='sm'
          />
        </div>
        <div className='space-y-1.5'>
          <Label>DME & Supplies</Label>
          <Textarea
            {...register('dme_supplies')}
            placeholder='Enter DME and supplies'
            textareaSize='sm'
          />
        </div>
        <div className='space-y-1.5'>
          <Label>Prognosis</Label>
          <Textarea
            {...register('prognosis')}
            placeholder='Enter prognosis'
            textareaSize='sm'
          />
        </div>
        <div className='space-y-1.5'>
          <Label>Safety Measures</Label>
          <Textarea
            {...register('safety_measures')}
            placeholder='Enter safety measures'
            textareaSize='sm'
          />
        </div>
        <div className='space-y-1.5'>
          <Label>Nutritional Requirements</Label>
          <Textarea
            {...register('nutritional_requirements')}
            placeholder='Enter nutritional requirements'
            textareaSize='sm'
          />
        </div>
        <div className='space-y-1.5'>
          <Label>Functional Limitations</Label>
          <Textarea
            {...register('functional_limitations')}
            placeholder='One item per line'
            textareaSize='sm'
          />
        </div>
        <div className='space-y-1.5'>
          <Label>Activities Permitted</Label>
          <Textarea
            {...register('activities_permitted')}
            placeholder='One item per line'
            textareaSize='sm'
          />
        </div>

        <SectionTitle>Treatments</SectionTitle>
        <div className='space-y-1.5'>
          <Label>Medications</Label>
          <Textarea
            {...register('medications')}
            placeholder='One medication per line'
            textareaSize='sm'
          />
        </div>
        <div className='space-y-1.5'>
          <div className='flex items-center justify-between gap-2'>
            <Label>Allergies</Label>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 px-2 text-xs text-muted-foreground'
              onClick={() => appendAllergy({ substance: '', reaction: '' })}
            >
              Add allergy
            </Button>
          </div>
          {allergyFields.length ? (
            <div className='divide-y divide-border/50'>
              {allergyFields.map((field, index) => (
                <div
                  key={field.id}
                  className='grid gap-2 py-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end first:pt-0'
                >
                  <div className='space-y-1'>
                    <Label className='text-xs text-muted-foreground'>
                      Substance
                    </Label>
                    <Input
                      {...register(`allergies.${index}.substance`)}
                      placeholder='Substance'
                      inputSize='sm'
                    />
                  </div>
                  <div className='space-y-1'>
                    <Label className='text-xs text-muted-foreground'>
                      Reaction
                    </Label>
                    <Input
                      {...register(`allergies.${index}.reaction`)}
                      placeholder='Reaction'
                      inputSize='sm'
                    />
                  </div>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='h-8 px-2 text-xs text-muted-foreground'
                    onClick={() => removeAllergy(index)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className='text-xs text-muted-foreground'>
              No allergies documented.
            </p>
          )}
        </div>
        <div className='space-y-1.5'>
          <div className='flex items-center justify-between gap-2'>
            <Label>Orders & Treatments</Label>
            {renderSectionPrefillButton('orders_and_treatments')}
          </div>
          <Textarea
            {...register('orders_and_treatments')}
            placeholder='Enter orders and treatments'
            textareaSize='smDocument'
          />
        </div>
        <div className='space-y-1.5'>
          <div className='flex items-center justify-between gap-2'>
            <Label>Goals & Outcomes</Label>
            {renderSectionPrefillButton('goals')}
          </div>
          <Textarea
            {...register('goals')}
            placeholder='Enter goals and measurable outcomes'
            textareaSize='smDocument'
          />
        </div>

        <div className='space-y-1.5 border-t pt-4'>
          <div className='flex items-center justify-between gap-2'>
            <Label>Homebound Certification Statement</Label>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={requestStandardHomeboundTemplate}
            >
              Insert standard template
            </Button>
          </div>
          <Textarea
            {...register('homebound_certification_statement')}
            placeholder='Enter homebound certification statement'
            textareaSize='smNarrative'
          />
        </div>
      </CardContent>
    </Card>
  );
};
